import BagongPilipinasLogo from '@/assets/BAGONG-PILIPINAS-LOGO.png';
import DENRLogo from '@/assets/denr-logo.png';
import { format } from 'date-fns';
import { forwardRef, useEffect } from 'react';
import QRCode from 'react-qr-code';

const CSAWCertificateTemplate = forwardRef(({ certificate, application, orderOfPayment, hiddenOnPrint = [] }, ref) => {
   // Create a properly formatted URL for the QR code - will work on all platforms
   const qrValue = `${window.location.origin}/verify-certificate/${certificate.id}`;

   // For debugging, log the QR value
   useEffect(() => {
      console.log('QR Code value:', qrValue);
   }, [qrValue]);

   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return null;

         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }

         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }

         if (timestamp instanceof Date) {
            return format(timestamp, 'MMMM d, yyyy');
         }

         return null;
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return null;
      }
   };

   return (
      <div ref={ref} className="p-8 bg-white dark:bg-gray-900 dark:text-gray-100">
         <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-4 mb-4">
               <img src={DENRLogo} alt="DENR Logo" className="h-20 dark:invert" />
               <div>
                  <h1 className="text-xl font-bold dark:text-gray-100">CERTIFICATE OF REGISTRATION</h1>
                  <p className="text-sm dark:text-gray-300">Department of Environment and Natural Resources</p>
                  <p className="text-sm dark:text-gray-300">PENRO Marinduque</p>
               </div>
               <img src={BagongPilipinasLogo} alt="GovPH Logo" className="h-20 dark:invert" />
            </div>
            <p className="text-sm dark:text-gray-300">No. {certificate.certificateNumber}</p>
         </div>

         <div className="mb-6">
            <p className="text-justify mb-4 dark:text-gray-300">
               After having complied with the provisions of DENR Administrative Order No. 2003-24, Series of 2003
               otherwise known as "The Implementing Rules and Regulations of the Chainsaw Act of 2002 (RA No. 9175)"
               entitled "AN ACT REGULATING THE OWNERSHIP, POSSESSION, SALE, IMPORTATION AND USE OF CHAINSAWS
               PENALIZING VIOLATIONS THEREOF AND FOR OTHER PURPOSES" this Certificate of Registration to own,
               possess and/or use a chainsaw is hereby issued to:
            </p>
         </div>

         <div className="mb-6">
            <p className="font-bold text-center mb-4 dark:text-gray-100">{application?.ownerName}</p>
            <p className="text-center mb-4 dark:text-gray-300">(Name of Owner)</p>
            <p className="text-center mb-4 dark:text-gray-300">{application?.address}</p>
            <p className="text-center dark:text-gray-300">(Address)</p>
         </div>

         <div className="mb-6">
            <p className="mb-2 dark:text-gray-300">Bearing the following information and descriptions:</p>
            <p className="mb-2 dark:text-gray-300">
               <span className="font-bold dark:text-gray-100">Use of the Chainsaw:</span> {application?.purpose}
            </p>

            <div className="grid grid-cols-2 gap-4">
               <div>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Brand:</span> {application?.chainsawDetails?.brand}</p>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Model:</span> {application?.chainsawDetails?.model}</p>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Serial No:</span> {application?.chainsawDetails?.serialNumber}</p>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Date of Acquisition:</span> {formatDate(application?.chainsawDetails?.dateOfAcquisition)}</p>
               </div>
               <div>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Power Output:</span> {application?.chainsawDetails?.powerOutput}</p>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Maximum Length of Guidebar:</span> {application?.chainsawDetails?.maxLengthGuidebar}</p>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Country of Origin:</span> {application?.chainsawDetails?.countryOfOrigin}</p>
                  <p className="dark:text-gray-300"><span className="font-bold dark:text-gray-100">Purchase Price:</span> ₱{
                     typeof application?.chainsawDetails?.purchasePrice === 'number'
                        ? application.chainsawDetails.purchasePrice.toFixed(2)
                        : '0.00'
                  }</p>
               </div>
            </div>
         </div>

         <div className="mb-6">
            <p className="mb-2 dark:text-gray-300">
               Issued on: {certificate.dateIssued ?
                  formatDate(certificate.dateIssued) :
                  '___________________'} at DENR-PENRO Marinduque
            </p>
            <p className="dark:text-gray-300">
               Expiry Date: {certificate.expiryDate ?
                  formatDate(certificate.expiryDate) :
                  '_____________________'}
            </p>
         </div>

         <div className="mb-6">
            <p className="font-bold mb-4 dark:text-gray-100">APPROVED BY:</p>
            <div className="mt-12">
               <div className="signature-box text-center">
                  {certificate?.penrCenrOfficerSignature?.data && (
                     <div className="signature-line mb-2">
                        <img
                           src={`data:${certificate.penrCenrOfficerSignature.contentType};base64,${certificate.penrCenrOfficerSignature.data}`}
                           alt="PENR Officer Signature"
                           className="mx-auto h-16 object-contain dark:invert"
                        />
                     </div>
                  )}
                  <p className="font-bold text-center dark:text-gray-100">IMELDA M. DIAZ</p>
                  <p className="text-center dark:text-gray-300">OIC-PENR Officer</p>
               </div>
            </div>
         </div>

         <div className="flex justify-between items-end">
            <div>
               <p className="dark:text-gray-300">Amount paid: Php {orderOfPayment?.officialReceipt?.amount?.toFixed(2) || '500.00'}</p>
               <p className="dark:text-gray-300">O.R. No.: {orderOfPayment?.officialReceipt?.orNumber || '_______________'}</p>
               <p className="dark:text-gray-300">Date: {orderOfPayment?.officialReceipt?.dateIssued ?
                  formatDate(orderOfPayment.officialReceipt.dateIssued) :
                  '_________________'}</p>
            </div>
            <div className={`qr-code ${hiddenOnPrint.includes('qr-code') ? 'no-print' : ''}`}>
               <QRCode
                  value={qrValue}
                  size={100}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  viewBox={`0 0 100 100`}
                  className="dark:bg-white dark:p-2 dark:rounded"
               />
               <p className="text-xs mt-2 dark:text-gray-300">Scan to verify</p>
            </div>
         </div>

         <div className="mt-8 text-xs text-center">
            <p className="dark:text-gray-300">***Not valid w/o PENRO Official Seal***</p>
            <p className="mt-4 dark:text-gray-300">
               Capitol Compound, Barangay Bangbangalon, Boac, Marinduque<br />
               Telephone Nos.: (042) 332-1490/0917-552-6811 VOIP: 2305<br />
               Website: https://penromarinduque.gov.ph<br />
               Email: penromarinduque@denr.gov.ph
            </p>
         </div>
      </div>
   );
});

CSAWCertificateTemplate.displayName = 'CSAWCertificateTemplate';

export default CSAWCertificateTemplate;
